import React, { useState } from 'react';
import { Box, Typography, Collapse, Paper, IconButton, Stack, Button } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { alpha, useTheme } from '@mui/material/styles';
import { useConfig } from '../../context/ConfigContext';
import { useSession } from '../../context/SessionContext';

const AILimitationsNotice = ({ onDismiss }) => {
  const [expanded, setExpanded] = useState(false);
  const { aiLimitationsNotice, chatTeamConfigs } = useConfig();
  const { teamName } = useSession();
  const theme = useTheme();

  // If the app hasn't configured a notice, don't render anything
  if (!aiLimitationsNotice) return null;

  // Check if the current team explicitly disabled the notice (e.g., onboarding)
  const teamConfig = chatTeamConfigs?.[teamName];
  if (teamConfig?.disableAILimitationsNotice) return null;

  const { title, summary, items, footer, content } = aiLimitationsNotice;

  return (
    <Box sx={{ px: 2, pt: 2, pb: 0 }}>
      <Paper
        variant="outlined"
        onClick={() => setExpanded(!expanded)}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          backgroundColor: alpha(theme.palette.warning.main, 0.05),
          borderColor: alpha(theme.palette.warning.main, 0.3),
          borderRadius: 2,
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
          }
        }}
      >
        {/* Collapsed header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20, mt: '2px' }} />
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {title || "AI Limitations Notice"}
              </Typography>
              {!expanded && summary && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {summary}
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconButton size="small" sx={{ p: 0, color: 'text.secondary', ml: 1, flexShrink: 0 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>

        {/* Expanded content */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 1.5, pl: 3.5 }}>
            {items && items.length > 0 ? (
              <>
                {items.map((item, index) => (
                  <Box key={index} sx={{ mb: index < items.length - 1 ? 1.5 : 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, lineHeight: 1.5 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                ))}

                {footer && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      lineHeight: 1.5,
                    }}
                  >
                    {footer}
                  </Typography>
                )}
              </>
            ) : (
              // Backward compatible: plain string fallback
              <Typography variant="body2" color="text.secondary">
                {content}
              </Typography>
            )}

            {/* Dismiss button */}
            {onDismiss && (
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss();
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    px: 2,
                  }}
                >
                  I understand — don't show again
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default AILimitationsNotice;